import { AsyncPipe, NgIf, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MemberRole } from '@picflow/types';
import { Observable, combineLatest, map, of, switchMap } from 'rxjs';
import { GalleryMemberService } from 'src/app/gallery-member.service';
import { TenantService } from 'src/app/services/tenant.service';
import { DisplayGuestEmailDto, DisplayUserEmailDto } from 'src/app/services/visitor.service';
import { ButtonDirective } from 'src/app/shared/directives/button.directive';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';

@Component({
  selector: 'app-profile-preview',
  standalone: true,
  templateUrl: './profile-preview.component.html',
  styleUrls: ['./profile-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, AsyncPipe, UpperCasePipe, AvatarPipe, RouterLink, ButtonDirective, AvatarComponent],
})
export class ProfilePreviewComponent {
  @Input() user: DisplayUserEmailDto;
  @Input() guest: DisplayGuestEmailDto;
  @Input() email: string;

  private tenantService = inject(TenantService);
  private galleryMemberService = inject(GalleryMemberService);

  role$ = of(null).pipe(
    switchMap((): Observable<MemberRole | 'guest' | 'visitor'> => {
      if (this.guest) return of('visitor');
      return combineLatest([
        this.tenantService.getMembers(),
        this.galleryMemberService.getAllGalleryMembers(true),
      ]).pipe(
        map(([teamMembers, galleryMembers]) => {
          const myId = this.user?.id || this.guest['id'];
          const teamMember = teamMembers.find(m => m.id === myId);
          if (teamMember) return teamMember.role;
          const galleryMember = galleryMembers.find(m => m.user['id'] === myId);
          if (galleryMember) return 'guest';
          return 'visitor';
        })
      );
    })
  );

  get labelColor(): Observable<string> {
    return this.role$.pipe(map(role => this.getLabelColor(role)));
  }

  getLabelColor(role: MemberRole | 'guest' | 'visitor') {
    if (role === 'visitor') return 'grey-50';
    if (role === 'guest') return 'orange';
    if (role === 'member') return 'primary';
    return 'dark';
  }
}
